import React from 'react';
import Alert from 'react-bootstrap/Alert';
import { RecipeType } from '../../../enums/RecipeType';
import { Recipe } from '../../../models/Recipe';
import { strings } from '../../../resources/strings';
import { recipeResources } from '../../../resources/recipeResources';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { Button } from '../../../common-components/Button/Button';
import { CollapsibleCard } from '../../../common-components/CollapsibleCard/CollapsibleCard';
import { Table } from '../../../common-components/Table/Table';
import { TableRow } from '../../../common-components/TableRow/TableRow';
import { TableHead } from '../../../common-components/TableHead/TableHead';
import { TableData } from '../../../common-components/TableData/TableData';
import {
  DiagnosticBlock, DiagnosticContainerTable,
  DiagnosticDistanceType, RecipeFileBlock, RecipeIdentification, RecipeTypeDescription
} from './styles';

interface IRecipeCardProps {
  recipe: Recipe;
  deleteRecipe?: () => void;
}

const FixedTableHeadDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableHead>;

const TableHeadDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' textAlign='center'>{children}</TableHead>;

const FixedTableDataDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableData borderStyle='1px solid #ddd' textAlign='center'>{children}</TableData>;

const TableDataDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableData borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableData>;

export const RecipeCard: React.FC<IRecipeCardProps> = ({
  recipe,
  deleteRecipe
}) => {

  let identification = '';
  if (recipe.customerName && recipe.recipeApprovedAt) {
    identification = `${recipe.customerName} - ${FormatHelper.formatDateTime(recipe.recipeApprovedAt)}`;
  } else if (recipe.customerName) {
    identification = `${recipe.customerName}`;
  } else if (recipe.recipeApprovedAt) {
    identification = FormatHelper.formatDateTime(recipe.recipeApprovedAt);
  } else {
    identification = 'Receita';
  }

  const header = <>
    <RecipeTypeDescription>
      {`${strings.Recipe}/${strings.Order}`} {recipe.recipeType === RecipeType.Bifocal ? recipeResources.BifocalRecipeTypeDescription : recipeResources.SingleVisionRecipeTypeDescription}
    </RecipeTypeDescription>
    <RecipeIdentification>
      {identification}
    </RecipeIdentification>
    <div></div>
  </>;

  const buttons = <>
    <Button text={strings.Delete} minWidth={'100px'} type='secondary' click={deleteRecipe} />
  </>;

  const recipeFile = React.useMemo(() => {
    return (
      <RecipeFileBlock>
        {recipe.pathToFile
          ? <a href={`${recipe?.pathToFile}`} target='_blank'>Arquivo de receita anexo</a>
          : <span>Esta receita não possui arquivo anexo</span>}
      </RecipeFileBlock>
    )
  }, [recipe]);

  const diagnostics = React.useMemo(() => {

    if (recipe.diagnosticsInformations.length === 0) {
      return <Alert variant='warning' style={{ marginTop: '10px' }}>A receita ainda não possui diagnóstico.</Alert>;
    }

    return (
      recipe.diagnosticsInformations.map(diagnostic => {
        const distanceTypeDescription = recipeResources.DistanceTypeOptions.find(rto => rto.Type === diagnostic.distanceType)?.Description;
        return (
          <DiagnosticBlock key={`diagnostic-${diagnostic.id}`}>
            <DiagnosticDistanceType>{distanceTypeDescription}</DiagnosticDistanceType>
            <DiagnosticContainerTable>
              <Table>
                <thead>
                  <TableRow>
                    <FixedTableHeadDiagnostic>{strings.Eye}</FixedTableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Spherical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Cylindrical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Axis}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.DNP}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Height}</TableHeadDiagnostic>
                  </TableRow>
                </thead>
                <tbody>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Right}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.rightEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Left}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.leftEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                </tbody>
              </Table>
            </DiagnosticContainerTable>
          </DiagnosticBlock>
        )
      })
    );
  }, [recipe]);

  const multifocalInfo = React.useMemo(() => {
    if (!recipe.multifocalInfo) return null;

    return (
      <DiagnosticBlock>
        <DiagnosticDistanceType>Multifocal</DiagnosticDistanceType>
        <DiagnosticContainerTable>
          <Table>
            <thead>
              <TableRow>
                <TableHeadDiagnostic>{strings.Addition}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Bridge}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Rim}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Vertical}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.AbbreviationForLargestDiagonal}</TableHeadDiagnostic>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <TableDataDiagnostic>{recipe.multifocalInfo?.addField}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.multifocalInfo?.actualBridge}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.multifocalInfo?.actualEyeWidth}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.multifocalInfo?.actualEyeHeight}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.multifocalInfo?.actualLargestDiagonal}</TableDataDiagnostic>
              </TableRow>
            </tbody>
          </Table>
        </DiagnosticContainerTable>
      </DiagnosticBlock>
    );
  }, [recipe]);

  return (
    <CollapsibleCard
      showExpanded={false}
      header={header}
      buttons={buttons}
      marginBottom='5px'
    >
      {recipeFile}
      {diagnostics}
      {multifocalInfo}
    </CollapsibleCard>
  );
}